import React, {
  lazy, Suspense, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import GDALogo from '../../svg/logos/ResyGDAExclusivelyForAmex.svg';
import PriorityNotifyIcon from '../../svg/icons/gda_priority_notify_icon.svg';
import ExclusiveOffersIcon from '../../svg/icons/gda_exclusive_offers_icon.svg';
import SpecialExperienceIcon from '../../svg/icons/gda_special_experience_icon.svg';
import Link from '../Link';
import Button from '../Button';
import { getTranslation } from '../../providers/TranslationProvider';
import announcementTypes, { EVENT_TYPE } from '../Announcement/announcementTypes';

import './NotifyGdaPage.scss';
import { focusTrap } from '../../utils';

const CloseIconMobile = lazy(() => import('../../svg/icons/ic_close_20x20.svg'));
const CloseIconDesktop = lazy(() => import('../../svg/icons/ic_close_alt_20x20.svg'));

const iconList = [
  <PriorityNotifyIcon role="img" aria-label={getTranslation({ translationKey: 'gda_priority_notify_icon', fallback: 'Priority notify icon' })} />,
  <ExclusiveOffersIcon role="img" aria-label={getTranslation({ translationKey: 'gda_exclusive_offer_icon', fallback: 'Exclusive reservations icon' })} />,
  <SpecialExperienceIcon role="img" aria-label={getTranslation({ translationKey: 'gda_special_experience_icon', fallback: 'Special experiences icon' })} />,
];
const PAGE_CONTEXT = 'set notify';
const CTA_TYPE = 'primary';

const NotifyGdaPage = ({
  Analytics, imageUrl, header, id, contentList, ctaLabel, ctaLink, isMobile, onClose, onCtaClick,
}) => {
  const getEventProperties = () => ({
    context: PAGE_CONTEXT,
    type: announcementTypes.POPUP,
    title: header,
    id,
  });

  useEffect(() => {
    focusTrap.focusModal('.NotifyGdaPage');
    Analytics.event(EVENT_TYPE.VIEW_ANNOUNCEMENT, getEventProperties());
  }, []);

  const handleCtaClick = () => {
    onCtaClick?.();

    Analytics.event(EVENT_TYPE.CLICK_ANNOUNCEMENT, {
      ...getEventProperties(),
      cta: CTA_TYPE,
    });
  };

  const closeButtonLabel = onClose
    ? getTranslation({
      translationKey: 'close',
      fallback: 'Close',
    })
    : null;

  return (
    <section role="alertdialog" aria-label="Notify GDA Page" className="NotifyGdaPage">
      <div className="NotifyGdaPage__Close-Button-Wrapper">
        <Button onClick={onClose} variant="no-style" aria-label={closeButtonLabel}>
          <Suspense fallback={null}>
            {isMobile ? <CloseIconMobile /> : <CloseIconDesktop />}
          </Suspense>
        </Button>
      </div>
      <div className="NotifyGdaPage__Image-Wrapper">
        <img src={imageUrl} alt={header} />
      </div>
      <div className="NotifyGdaPage__Main">
        <div className="NotifyGdaPage__Logo-Wrapper">
          <GDALogo role="img" aria-label={getTranslation({ translationKey: 'gda_by_resy_logo', fallback: 'Global Dining Access by Resy logo' })} />
        </div>
        <div className="NotifyGdaPage__Header-Wrapper">
          <h1 className="NotifyGdaPage__Header">{header}</h1>
        </div>
        <div className="NotifyGdaPage__Content-Wrapper">
          <ul>
            {contentList.map((content, index) => (
              <li key={content}>
                <span>{iconList[index]}</span>
                <span>{content}</span>
              </li>
            ))}
          </ul>
        </div>
        {ctaLink ? (
          <div className="NotifyGdaPage__Footer-Wrapper">
            <Link variant="button-secondary" href={ctaLink} onClick={handleCtaClick} target="_blank">
              {ctaLabel}
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export const NotifyGdaPagePropType = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.number.isRequired,
  imageUrl: PropTypes.string.isRequired,
  header: PropTypes.string.isRequired,
  contentList: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  ctaLabel: PropTypes.string,
  ctaLink: PropTypes.string,
  isMobile: PropTypes.bool,
  onCtaClick: PropTypes.func,
};

NotifyGdaPage.propTypes = NotifyGdaPagePropType;

NotifyGdaPage.defaultProps = {
  contentList: [],
  ctaLabel: undefined,
  ctaLink: undefined,
  isMobile: false,
  onCtaClick: undefined,
};

NotifyGdaPage.displayName = 'NotifyGdaPage';
export default NotifyGdaPage;
